// Madness Control Effect
// Features: The chaotic Eyes of Madness spawn, but are immediately restrained
// by heavy bronze calipers. Bronze tubes shoot out, connecting to the caster
// and systematically siphoning their chaotic energy.

const CONFIG = {
    eyeCount: 10,
    scale: 1.2,
    openDuration: 1000,
    stareDuration: 2500, // Longer stare/siphon phase
    fadeDuration: 1000,
    maxDelay: 1900,
};

// --- Sprite Caching ---
const spriteCache = new Map<string, HTMLCanvasElement>();

function getCaliperSprite(isTop: boolean): HTMLCanvasElement {
    const key = `caliper-${isTop ? 'top' : 'bot'}`;
    let sprite = spriteCache.get(key);
    if (!sprite) {
        sprite = document.createElement('canvas');
        const W = 500;
        const H = 600;
        sprite.width = W;
        sprite.height = H;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const cx = W / 2;
            const cy = H / 2;
            const r = 130;

            ctx.translate(cx, cy);

            ctx.beginPath();
            if (isTop) {
                ctx.rect(-r - 100, -r - 150, (r + 100) * 2, r + 150 + 20);
            } else {
                ctx.rect(-r - 100, -20, (r + 100) * 2, r + 150 + 20); // lower half + 20px overlap
            }
            ctx.clip();

            // --- Base Concentric Rings ---
            const baseGrad = ctx.createLinearGradient(0, -r, 0, r);
            baseGrad.addColorStop(0, '#e8c17a');
            baseGrad.addColorStop(0.3, '#9c703f');
            baseGrad.addColorStop(0.7, '#4a3219');
            baseGrad.addColorStop(1, '#261709');

            ctx.fillStyle = baseGrad;
            ctx.strokeStyle = '#1a0f05';
            ctx.lineWidth = 3;

            // Outer massive ring
            ctx.beginPath();
            ctx.arc(0, 0, r, 0, Math.PI * 2);
            // Inner hole for the eye (with teeth)
            ctx.arc(0, 0, r - 40, 0, Math.PI * 2, true);
            ctx.fill();
            ctx.stroke();

            // Inner Bevel Ring (Stepped depth)
            const innerGrad = ctx.createLinearGradient(0, r, 0, -r);
            innerGrad.addColorStop(0, '#e8c17a');
            innerGrad.addColorStop(0.5, '#7a5229');
            innerGrad.addColorStop(1, '#261709');

            ctx.fillStyle = innerGrad;
            ctx.beginPath();
            ctx.arc(0, 0, r - 40, 0, Math.PI * 2);
            ctx.arc(0, 0, r - 50, 0, Math.PI * 2, true);
            ctx.fill();
            ctx.stroke();

            // --- Blue Glassy Lens (From Reference) ---
            const lensGrad = ctx.createLinearGradient(0, -r, 0, r);
            lensGrad.addColorStop(0, 'rgba(0, 191, 255, 0.9)');
            lensGrad.addColorStop(0.5, 'rgba(0, 255, 255, 0.9)');
            lensGrad.addColorStop(1, 'rgba(0, 95, 191, 0.9)');

            ctx.fillStyle = lensGrad;
            ctx.beginPath();
            ctx.arc(0, 0, r - 48, 0, Math.PI * 2);
            ctx.arc(0, 0, r - 75, 0, Math.PI * 2, true);
            ctx.fill();

            // Lens Inner Glow/Bevel
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.beginPath();
            ctx.arc(0, 0, r - 73, 0, Math.PI * 2);
            ctx.stroke();

            // Faded alien/runic tech lines inside the blue glass
            ctx.strokeStyle = 'rgba(0, 50, 150, 0.4)';
            ctx.lineWidth = 1;
            for (let i = 0; i < 24; i++) {
                ctx.save();
                ctx.rotate((i / 24) * Math.PI * 2);
                ctx.beginPath();
                ctx.moveTo(r - 50, 0);
                ctx.quadraticCurveTo(r - 60, 15, r - 70, 5);
                ctx.stroke();
                ctx.restore();
            }

            // Engraved Lines / Details on the outer ring
            ctx.strokeStyle = '#261709';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(0, 0, r - 20, 0, Math.PI * 2);
            ctx.stroke();

            // Add some crack/runic detailing around the ring based on the reference
            if (isTop) {
                ctx.beginPath(); ctx.moveTo(-60, -r + 15); ctx.lineTo(-40, -r + 25); ctx.lineTo(-45, -r + 35); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(70, -r + 10); ctx.lineTo(60, -r + 25); ctx.stroke();
            } else {
                // panel lines
                for (let i = -60; i <= 60; i += 30) {
                    ctx.beginPath(); ctx.moveTo(i, r - 5); ctx.lineTo(i, r - 15); ctx.stroke();
                }
            }

            // Side Spikes / Claws (Reference shows protruding horns on sides)
            ctx.fillStyle = baseGrad;
            if (isTop) {
                // Right Top Claw
                ctx.beginPath();
                ctx.moveTo(r - 10, -10);
                ctx.quadraticCurveTo(r + 60, -20, r + 70, 30);
                ctx.quadraticCurveTo(r + 30, 20, r + 5, 10);
                ctx.fill(); ctx.stroke();

                // Left Top Claw
                ctx.beginPath();
                ctx.moveTo(-r + 10, -10);
                ctx.quadraticCurveTo(-r - 60, -20, -r - 70, 30);
                ctx.quadraticCurveTo(-r - 30, 20, -r - 5, 10);
                ctx.fill(); ctx.stroke();
            } else {
                // Right Bottom Block
                ctx.fillRect(r - 5, 10, 25, 30);
                ctx.strokeRect(r - 5, 10, 25, 30);

                // Left Bottom Block
                ctx.fillRect(-r - 20, 10, 25, 30);
                ctx.strokeRect(-r - 20, 10, 25, 30);
            }

            // Top Adornments (The Three Ornate Tubes from the reference)
            if (isTop) {
                const tubeGrad = ctx.createLinearGradient(0, -r - 80, 0, -r);
                tubeGrad.addColorStop(0, '#e8c17a');
                tubeGrad.addColorStop(1, '#261709');
                ctx.fillStyle = tubeGrad;

                const drawTube = (angle: number, height: number, width: number) => {
                    ctx.save();
                    ctx.rotate(angle);
                    ctx.translate(0, -r + 5);
                    ctx.beginPath();
                    ctx.moveTo(-width / 2, 0);
                    // Wavy trumpet shape
                    ctx.quadraticCurveTo(-width * 0.8, -height / 2, -width, -height);
                    ctx.lineTo(width, -height);
                    ctx.quadraticCurveTo(width * 0.8, -height / 2, width / 2, 0);
                    ctx.fill(); ctx.stroke();

                    // Swirl top detail
                    ctx.beginPath();
                    ctx.arc(0, -height + 10, width * 0.6, 0, Math.PI * 2);
                    ctx.fill(); ctx.stroke();

                    ctx.restore();
                };

                // Center tube
                drawTube(0, 80, 25);
                // Left tube
                drawTube(-0.35, 70, 20);
                // Right tube
                drawTube(0.35, 70, 20);
            }

            // Interlocking teeth along the cut line
            ctx.fillStyle = '#1a0f05';
            ctx.strokeStyle = '#3B2B18';
            ctx.lineWidth = 2;
            for (let x = -r + 15; x < r - 15; x += 30) {
                ctx.beginPath();
                if (isTop) {
                    ctx.moveTo(x + 5, -2);
                    ctx.lineTo(x + 15, 15);
                    ctx.lineTo(x + 25, -2);
                } else {
                    ctx.moveTo(x - 10, 2);
                    ctx.lineTo(x, -15);
                    ctx.lineTo(x + 10, 2);
                }
                ctx.fill(); ctx.stroke();
            }
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

function getMadnessEyeSprite(openProgress: number): HTMLCanvasElement {
    // Quantize progress to reduce cache misses (0 to 1, in steps of 0.05)
    let p = Math.round(openProgress * 20) / 20;
    if (p < 0) p = 0;
    if (p > 1) p = 1;

    const key = `madEye-control-${p}`;
    let sprite = spriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const W = 300;
        const H = 200;
        sprite.width = W;
        sprite.height = H;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const cx = W / 2;
            const cy = H / 2;

            ctx.clearRect(0, 0, W, H);

            // The eye opening height depends on progress
            const eyeH = 60 * p;

            // Outer chaotic glow (Aura)
            if (p > 0) {
                const glowGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, 100 * p + 20);
                glowGrad.addColorStop(0, `rgba(255, 100, 0, ${p * 0.8})`);
                glowGrad.addColorStop(0.5, `rgba(200, 0, 0, ${p * 0.4})`);
                glowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
                ctx.fillStyle = glowGrad;
                ctx.beginPath();
                ctx.ellipse(cx, cy, 120 * p + 20, 100 * p + 20, 0, 0, Math.PI * 2);
                ctx.fill();
            }

            // The Eyelids / Eye shape
            if (p > 0.05) {
                ctx.save();
                ctx.beginPath();
                // Almond eye shape
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx - 100, cy);
                ctx.closePath();
                ctx.clip(); // Clip to eye bounds

                // Sclera (white/yellowish inner background) -> smooth glowing gradient
                const scleraGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120);
                scleraGrad.addColorStop(0, '#ffff55'); // Bright, almost white-yellow center
                scleraGrad.addColorStop(0.2, '#ffcc00'); // Core bright yellow
                scleraGrad.addColorStop(0.6, '#ff4400'); // Deep orange
                scleraGrad.addColorStop(1, '#880000'); // Dark red edges
                ctx.fillStyle = scleraGrad;
                ctx.fill();

                // Inner Eye Shadow
                const shadowGrad = ctx.createRadialGradient(cx, cy, 40, cx, cy, 100);
                shadowGrad.addColorStop(0, 'rgba(0,0,0,0)');
                shadowGrad.addColorStop(1, 'rgba(0,0,0,0.8)');
                ctx.fillStyle = shadowGrad;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx - 100, cy);
                ctx.fill();

                ctx.restore();

                // Eyelid borders
                ctx.strokeStyle = '#cc1100';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.stroke();

                ctx.strokeStyle = '#660000';
                ctx.lineWidth = 5;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx + 100, cy);
                ctx.stroke();
            }
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

// --- Bronze Steampunk Mechanism Rendering ---
function drawBronzeCalipers(ctx: CanvasRenderingContext2D, snapProgress: number, scale: number) {
    // snapProgress: 0 (open/away) to 1 (clamped shut)
    ctx.save();
    ctx.scale(scale, scale);

    // Draw top half
    const topSprite = getCaliperSprite(true);
    const topOffset = -100 * (1 - snapProgress);
    ctx.drawImage(topSprite, -topSprite.width / 2, -topSprite.height / 2 + topOffset);

    // Draw bottom half
    const botSprite = getCaliperSprite(false);
    const botOffset = 100 * (1 - snapProgress);
    ctx.drawImage(botSprite, -botSprite.width / 2, -botSprite.height / 2 + botOffset);

    // Outer Glow / Energy integration where they meet
    const r = 130;
    if (snapProgress >= 1) {
        ctx.beginPath();
        ctx.moveTo(-r, 0);
        ctx.lineTo(r, 0);
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.8)';
        ctx.lineWidth = 4;
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 15;
        ctx.stroke();
        ctx.shadowBlur = 0;
    }

    ctx.restore();
}

// --- Segmented Bronze Tube Rendering ---
class BronzeTube {
    startX: number; startY: number; targetX: number; targetY: number;
    points: { x: number, y: number }[] = [];
    isDeployed: boolean = false;
    deployProgress: number = 0; // 0 to 1

    constructor(startX: number, startY: number, targetX: number, targetY: number) {
        this.startX = startX;
        this.startY = startY;
        this.targetX = targetX;
        this.targetY = targetY;

        // Create a bezier curve for the tube
        // Add some sag/curve to it
        const dx = targetX - startX;
        const dy = targetY - startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const cp1x = startX + dx * 0.3 + (dy / dist) * 50;
        const cp1y = startY + dy * 0.3 - (dx / dist) * 50;

        const cp2x = startX + dx * 0.7 - (dy / dist) * 50;
        const cp2y = startY + dy * 0.7 + (dx / dist) * 50;

        const segments = 20;
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const x = Math.pow(1 - t, 3) * startX + 3 * Math.pow(1 - t, 2) * t * cp1x + 3 * (1 - t) * t * t * cp2x + t * t * t * targetX;
            const y = Math.pow(1 - t, 3) * startY + 3 * Math.pow(1 - t, 2) * t * cp1y + 3 * (1 - t) * t * t * cp2y + t * t * t * targetY;
            this.points.push({ x, y });
        }
    }

    update(dt: number, delayOver: boolean) {
        if (delayOver && this.deployProgress < 1) {
            this.deployProgress += dt * 0.003; // deploy speed
            if (this.deployProgress >= 1) {
                this.deployProgress = 1;
                this.isDeployed = true;
            }
        }
    }

    draw(ctx: CanvasRenderingContext2D, alpha: number, time: number) {
        if (this.deployProgress <= 0) return;

        ctx.save();
        ctx.globalAlpha = alpha;

        const renderPoints = Math.floor(this.points.length * this.deployProgress);
        if (renderPoints < 2) {
            ctx.restore();
            return;
        }

        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);
        for (let i = 1; i < renderPoints; i++) {
            ctx.lineTo(this.points[i].x, this.points[i].y);
        }

        // 1. Thick dark outer casing
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 14;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();

        // 2. Bronze plating
        ctx.strokeStyle = '#A38454';
        ctx.lineWidth = 10;
        ctx.stroke();

        // 3. Segmented joints (using dashed line)
        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);
        for (let i = 1; i < renderPoints; i++) {
            ctx.lineTo(this.points[i].x, this.points[i].y);
        }
        ctx.strokeStyle = '#3B2B18';
        ctx.lineWidth = 12;
        ctx.setLineDash([4, 16]); // 4px solid, 16px gap
        ctx.stroke();
        ctx.setLineDash([]); // reset

        // 4. Glowing core energy pulsing
        if (this.isDeployed) {
            ctx.beginPath();
            ctx.moveTo(this.points[0].x, this.points[0].y);
            for (let i = 1; i < renderPoints; i++) {
                ctx.lineTo(this.points[i].x, this.points[i].y);
            }
            ctx.strokeStyle = '#ffff55';
            ctx.lineWidth = 4;
            // The magic: dash offset moves backward to simulate energy flowing FROM eye TO caster
            const dashOffset = -(time * 0.1) % 100;
            ctx.setLineDash([15, 25]);
            ctx.lineDashOffset = dashOffset;

            ctx.shadowColor = '#ff4400';
            ctx.shadowBlur = 10;
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.shadowBlur = 0;

            // Add a small bright "core" that doesn't dash
            ctx.strokeStyle = 'rgba(255, 100, 0, 0.4)';
            ctx.lineWidth = 2;
            ctx.stroke();
        }

        ctx.restore();
    }
}

class FrictionSpark {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number;
    constructor(x: number, y: number) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 8;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 200 + Math.random() * 300;
        this.life = this.maxLife;
    }
    update(dt: number) {
        this.x += this.vx * (dt / 16);
        this.y += this.vy * (dt / 16);
        this.vy += 0.2; // gravity
        this.vx *= 0.95; // friction
        this.life -= dt;
    }
    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.life <= 0) return;
        const a = (this.life / this.maxLife) * alpha;
        ctx.globalAlpha = a;
        ctx.fillStyle = '#FFD700'; // Bright gold/yellow
        ctx.beginPath();
        // Spiky sparks
        ctx.ellipse(this.x, this.y, 4, 1, Math.atan2(this.vy, this.vx), 0, Math.PI * 2);
        ctx.fill();
    }
}

class ControlledEye {
    x: number; y: number; targetX: number; targetY: number; angle: number;
    scale: number; delay: number;
    tube: BronzeTube;
    snapProgress: number = 0; // 0 to 1
    sparks: FrictionSpark[] = [];

    constructor(targetPlayerX: number, targetPlayerY: number, canvasWidth: number, canvasHeight: number, delayMs: number, existingEyes: ControlledEye[]) {
        const minDistancePadding = 30; // padding between edges
        let candidateX, candidateY;
        let validPos = false;
        let attempts = 0;

        // Bell-curve-ish distribution to favor medium sizes, reducing tiny and giant
        const u = 1 - Math.random();
        const v = Math.random();
        const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
        let randomScale = 0.6 + z * 0.25;
        if (randomScale < 0.2) randomScale = 0.2;
        if (randomScale > 1.2) randomScale = 1.2;

        this.scale = randomScale * CONFIG.scale;
        const myRadius = 150 * this.scale;

        while (!validPos && attempts < 150) {
            candidateX = myRadius + Math.random() * (canvasWidth - myRadius * 2);
            candidateY = myRadius + Math.random() * (canvasHeight - myRadius * 2);

            validPos = true;
            for (const eye of existingEyes) {
                const dx = candidateX - eye.x;
                const dy = candidateY - eye.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const eyeRadius = 150 * eye.scale;

                if (dist < myRadius + eyeRadius + minDistancePadding) {
                    validPos = false;
                    break;
                }
            }
            attempts++;
        }

        this.x = candidateX!;
        this.y = candidateY!;
        this.targetX = targetPlayerX;
        this.targetY = targetPlayerY;
        this.delay = delayMs;

        this.angle = Math.atan2(targetPlayerY - this.y, targetPlayerX - this.x) + (Math.random() - 0.5) * 0.5;

        // The tube runs from this eye's center to the caster's center
        this.tube = new BronzeTube(this.x, this.y, this.targetX, this.targetY);
    }

    draw(ctx: CanvasRenderingContext2D, t: number, dt: number, globalAlpha: number) {
        if (t < this.delay) return; // hasn't appeared yet

        const localT = t - this.delay;

        // Open progress: 0 to 1
        let openProgress = 0;
        let fadeAlpha = 1;

        if (localT < CONFIG.openDuration) {
            openProgress = localT / CONFIG.openDuration;
            openProgress = 1 - Math.pow(1 - openProgress, 3); // Easing
        } else if (localT < CONFIG.openDuration + CONFIG.stareDuration) {
            openProgress = 1; // fully open
        } else {
            const fadeT = localT - (CONFIG.openDuration + CONFIG.stareDuration);
            fadeAlpha = 1 - (fadeT / CONFIG.fadeDuration);
            if (fadeAlpha < 0) fadeAlpha = 0;
            openProgress = 1; // Keep it fully "open" inside the calipers, but fade it out
        }

        if (openProgress <= 0 && fadeAlpha <= 0) return;

        // Calculate Caliper Snap Logic
        // Calipers snap exactly as the eye finishes opening (t = openDuration)
        if (localT > CONFIG.openDuration) {
            const snapT = localT - CONFIG.openDuration;
            // Very fast, brutal snap (150ms)
            this.snapProgress = Math.min(1, snapT / 150);

            // Generate sparks exactly as they slam shut
            if (this.snapProgress > 0.8 && this.snapProgress < 1 && Math.random() < 0.3) {
                this.sparks.push(new FrictionSpark(
                    this.x + (Math.random() - 0.5) * 150 * this.scale,
                    this.y + (Math.random() - 0.5) * 100 * this.scale
                ));
            }
        }

        ctx.save();
        ctx.globalAlpha = globalAlpha * fadeAlpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);
        ctx.scale(this.scale, this.scale);

        // Draw Eye
        const sprite = getMadnessEyeSprite(openProgress);
        // Dim the eye if it's being siphoned
        if (this.snapProgress >= 1) {
            ctx.globalAlpha *= 0.5; // lose its luster to the mechanism
        }
        ctx.drawImage(sprite, -sprite.width / 2, -sprite.height / 2);

        // Draw Calipers
        if (this.snapProgress > 0) {
            ctx.globalAlpha = globalAlpha * fadeAlpha; // restore mechanism alpha so it stays strong
            // inverse the base scaling so drawBronzeCalipers' internal scaling works independently of the eye rotation
            ctx.scale(1 / this.scale, 1 / this.scale);
            drawBronzeCalipers(ctx, this.snapProgress, this.scale);
        }

        ctx.restore();

        // Update and draw tube
        const tubeDeployStartDelay = CONFIG.openDuration + 150; // starts right after snap
        this.tube.update(dt, localT > tubeDeployStartDelay);
        this.tube.draw(ctx, globalAlpha * fadeAlpha, t);

        // Draw Sparks
        for (let i = this.sparks.length - 1; i >= 0; i--) {
            const s = this.sparks[i];
            s.update(dt);
            s.draw(ctx, globalAlpha * fadeAlpha);
            if (s.life <= 0) {
                this.sparks[i] = this.sparks[this.sparks.length - 1];
                this.sparks.pop();
            }
        }
    }
}


export type MadnessControlEffect = {
    kind: 'madnessControl'
    t: number
    cx: number  // Caster X
    cy: number  // Caster Y
    data?: {
        eyes: ControlledEye[]
        lastTime: number
    }
}

export const createMadnessControlEffect = (cx: number, cy: number): MadnessControlEffect => ({
    kind: 'madnessControl', t: 0, cx, cy
})

export const renderMadnessControlEffect = (
    eff: MadnessControlEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const { width, height } = ctx.canvas;

    if (!eff.data) {
        const eyes: ControlledEye[] = [];
        for (let i = 0; i < CONFIG.eyeCount; i++) {
            eyes.push(new ControlledEye(eff.cx, eff.cy, width, height, Math.random() * CONFIG.maxDelay, eyes));
        }

        eff.data = {
            eyes,
            lastTime: performance.now()
        };
    }

    const d = eff.data;
    const now = performance.now();
    const dt = now - d.lastTime;
    d.lastTime = now;

    const totalDuration = CONFIG.openDuration + CONFIG.stareDuration + CONFIG.fadeDuration + CONFIG.maxDelay + 100;

    if (eff.t > totalDuration) {
        return false;
    }

    // Vignette background darkening (steampunk/bronze tint)
    let bgAlpha = 0;
    if (eff.t < 1000) bgAlpha = eff.t / 1000 * 0.6;
    else if (eff.t < totalDuration - 1000) bgAlpha = 0.6;
    else bgAlpha = Math.max(0, (totalDuration - eff.t) / 1000 * 0.6);

    ctx.save();

    if (bgAlpha > 0.01) {
        const bgGrad = ctx.createRadialGradient(eff.cx, eff.cy, 50, eff.cx, eff.cy, Math.max(width, height) * 0.8);
        bgGrad.addColorStop(0, `rgba(50, 20, 0, ${bgAlpha * 0.5})`);
        bgGrad.addColorStop(1, `rgba(10, 5, 0, ${bgAlpha})`);
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
    }

    ctx.globalCompositeOperation = 'source-over'; // Tubes and calipers need solid source over

    // Draw eyes, calipers, logic, and tubes
    d.eyes.forEach(eye => {
        eye.draw(ctx, eff.t, dt, 1.0);
    });

    ctx.restore();

    return true;
}
